import type { PayloadAction, WritableDraft } from '@reduxjs/toolkit';
import { createSlice, nanoid } from '@reduxjs/toolkit';
import { toast } from 'sonner';
import { hasNestedKeys, EAvailableLanguageCodes, EQuestionType } from '@hubertai/screening-question-types';
import type {
  TAnyClientQuestionType,
  TAvailableLanguageCodes,
  EFormType,
  TSelectionCountCriteria,
  TNode,
  TemplateEmailSettingsDocument,
  TemplateReminderSettingsDocument,
  TemplateSmsSettingsDocument,
  TemplateVoiceSettingsDocument,
} from '@hubertai/screening-question-types';
import {
  appendToOutlineWithDedupRename,
  collectRuleJumpTargetIds,
  ensureRulesOverride,
  pushRuleToNodeParams,
  removeOneFromOutlineByPrefix,
  setRulesOnNodeParams,
  updateFollowupScoringCategory,
  updateNonFollowupScoringCategory,
} from './TemplateSliceHelper';
import { removeRulesByIds } from './ruleMerge';
import { makeid } from '@/utils/utils';
import {
  updateFormSliderEvaluationSummary,
  updateMultipleSelectEvaluationSummary,
  updateSingleSelectEvaluationSummary,
} from 'app/sideEffects';
import type { TSelectEvaluationSummary } from 'types/evaluation.types';
import type { TQuestionRule } from 'types/rules.types';
import { ERuleType, newAction, newCondition, ruleType1, ruleType2 } from 'types/rules.types';
import type {
  TTemplate,
  TTemplateBlock,
  TTemplateChangeLogItem,
  TTemplateParameterKeys,
  TVisibility,
} from 'types/template/template.types';
import { isQuickMessageNode } from 'types/template/quickMessage.types';
import type { TQuickMessageNode } from 'types/template/quickMessage.types';
import type { TTemplateInterviewSummaryBlock } from 'types/template/templateInterviewSummary.types';
import type { TTemplateAudienceProblem } from 'types/template/templateAudience.types';
import { withoutProblemsFixedBySharing } from 'utils/templateAudienceSharing';

type BasePayload = {
  templateId: string;
  eventType: string;
  description?: string;

  old_value?: any;
  new_value?: any;
  field_changed?: string;
  value?: any;
};

export enum ETemplateRevision {
  Current = 'current',
  Draft = 'draft',
  Process = 'process',
  Erroneous = 'erroneous',
}

// NOTE:
// Prefix "c" stands for "current"
export type initialTemplateSliceState = {
  cTemplateId: string;
  templateReferenceId: string;
  processId: string;
  isModified: boolean;
  isReadOnly: boolean;
  isDraft: boolean;
  isCurrent: boolean;
  isPublished: boolean;
  testJobId?: string;
  revision: ETemplateRevision;
  evaluationSummary: {
    [key: string]: TSelectEvaluationSummary;
  };
  cLanguage: {
    list: string[];
    current: string;
  };
  c: TTemplate;
  visibility?: TVisibility;
  hasQuestionsWithMissingLanguages?: boolean;
  audienceProblems: TTemplateAudienceProblem[];
  questionTranslationErrors: { questionId: string; message: string }[];
  questionTranslationResults: { questionId: string; languagesTo: string[] }[];
};

export const initialState: initialTemplateSliceState = {
  cTemplateId: '',
  processId: '',
  templateReferenceId: '',
  isModified: false,
  isReadOnly: false,
  isDraft: false,
  isCurrent: false,
  isPublished: false,
  revision: ETemplateRevision.Current,
  evaluationSummary: {},
  visibility: undefined,
  hasQuestionsWithMissingLanguages: false,
  audienceProblems: [],
  questionTranslationErrors: [],
  questionTranslationResults: [],

  cLanguage: { list: [], current: '' },
  // @ts-ignore
  c: {
    _id: '',
    template_reference_id: '',
    process_id: '',
    name: '',
    description: '',
    client_id: '',
    language_settings: {
      list: [],
      allowed_languages: [],
      starting_language: EAvailableLanguageCodes.enGB,
    },
    role: {
      id: '',
      name: '',
    },
    role_id: '',
    company: '',
    organization: {
      name: '',
      department: '',
    },
    title: '',
    position: {
      form_of_employment: '',
    },
    threshold: 0,
    estimated_length: 0,

    blocks: { blocks: [], ids: [''], map: {} },
    outline: { question_list: [] },
    evaluation: {
      scoring_categories: {
        list: [],
        structure: {},
      },
    },
    interview_summary: {
      summary_blocks: [],
    },
    version: 2,
    change_log: [],
    questions: [],
    parameters: {},
    is_draft: false,
    is_current: false,
    published: false,
    default: false,
    immediately_notify_unscored: false,
    candidate_app: false,
    script: false,
    accessGroupIds: [],
  },
};

const update = (state: WritableDraft<initialTemplateSliceState>) => ({
  removeNodeFromBlock(resource: { blockId: string; nodeId: string }) {
    const template = state.c;
    if (!template) {
      toast.error(`Template not available.`);
      return;
    }

    // template block
    const block = template.blocks.blocks.find((x) => x.id === resource.blockId);
    if (!block) {
      toast.error(`Cannot find the block.`);
      return;
    }

    // delete from nodes array
    const blockNodesIndex = block.nodes.findIndex((node: TNode) => node.id === resource.nodeId);
    if (blockNodesIndex > -1) {
      block.nodes.splice(blockNodesIndex, 1);
    } else {
      toast.error(`Node could not be found in the block.`);
    }

    // delete from general question list
    const questionListIndex = template.outline.question_list.findIndex((x: string) => x === resource.nodeId);
    if (questionListIndex > -1) {
      template.outline.question_list.splice(questionListIndex, 1);
    }

    // Set question as hidden in summary
    const summary = template.interview_summary;
    if (!summary) {
      toast.error(`Summary could not be found.`);
      return;
    }
  },
});

const TemplateSlice = createSlice({
  name: 'Template',
  initialState,
  reducers: {
    //////// General Functionality ////////

    addTemplate: (
      state,
      action: PayloadAction<{
        templateId: string;
        template: TTemplate;
        testJobId?: string;
        accessGroupIds?: string[];
        hasQuestionsWithMissingLanguages?: boolean;
        audienceProblems?: TTemplateAudienceProblem[];
      }>,
    ) => {
      const { templateId, template, testJobId, accessGroupIds, hasQuestionsWithMissingLanguages, audienceProblems } =
        action.payload;
      // Left out when the backend's own check failed, which says nothing about whether the problems it
      // last reported for this template have gone away.
      const lastKnownAudienceProblems = state.cTemplateId === templateId ? (state.audienceProblems ?? []) : [];
      const isSameTemplate = state.cTemplateId === templateId;
      const lastKnownAccessGroupIds = isSameTemplate ? (state.c.accessGroupIds ?? []) : [];
      const lastKnownHasQuestionsWithMissingLanguages = isSameTemplate
        ? (state.hasQuestionsWithMissingLanguages ?? false)
        : false;

      return {
        ...state,
        templateReferenceId: template.template_reference_id,
        cTemplateId: templateId,
        processId: template.process_id,
        isDraft: template.is_draft,
        isCurrent: template.is_current,
        testJobId,
        hasQuestionsWithMissingLanguages: hasQuestionsWithMissingLanguages ?? lastKnownHasQuestionsWithMissingLanguages,
        audienceProblems: audienceProblems ?? lastKnownAudienceProblems,
        isReadOnly: template.is_current || (!template.is_current && !template.is_draft),
        evaluationSummary: template.evaluationSummary,
        revision:
          template.is_current && !template.is_draft
            ? ETemplateRevision.Current
            : !template.is_current && template.is_draft
              ? ETemplateRevision.Draft
              : ETemplateRevision.Process,
        c: {
          ...template,
          accessGroupIds: accessGroupIds ?? lastKnownAccessGroupIds,
        },
        cLanguage: {
          // Preserve the language the user is currently viewing across a refetch of the same
          // template (e.g. after translating questions) instead of jumping back to the starting
          // language, which would mask missing translations for the language actually being viewed.
          current:
            state.cTemplateId === templateId &&
            state.cLanguage.current &&
            template.language_settings.list.includes(state.cLanguage.current as TAvailableLanguageCodes)
              ? state.cLanguage.current
              : template.language_settings.starting_language,
          list: template.language_settings.list,
        },
        visibility: template?.visibility,
      };
    },

    removeAudienceProblemsFixedBySharing: (state, action: PayloadAction<{ questionId: string }>) => {
      state.audienceProblems = withoutProblemsFixedBySharing(state.audienceProblems, [action.payload.questionId]);
    },

    updateProcessId: (state, action: PayloadAction<{ processId: string }>) => {
      const { processId } = action.payload;
      state.c.process_id = processId;
      state.processId = processId;
    },

    setCurrentTemplateLanguage: (state, action: PayloadAction<{ language: string }>) => {
      const { language } = action.payload;
      state.cLanguage.current = language;
    },

    setQuestionTranslationErrors: (
      state,
      action: PayloadAction<{ errors: { questionId: string; message: string }[] }>,
    ) => {
      state.questionTranslationErrors = action.payload.errors;
    },

    setQuestionTranslationResults: (
      state,
      action: PayloadAction<{ results: { questionId: string; languagesTo: string[] }[] }>,
    ) => {
      state.questionTranslationResults = action.payload.results;
    },

    updateTemplateLanguageList: (
      state,
      action: PayloadAction<
        {
          resource: { list: TAvailableLanguageCodes[] };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      if (resource.list && resource.list.length) {
        state.c.language_settings.list = resource.list;
        state.c.language_settings.starting_language = resource.list[0];
      }
    },

    updateTemplateAllowedLanguageList: (
      state,
      action: PayloadAction<
        {
          resource: { list: { code: string }[] };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      if (resource.list && resource.list.length) {
        state.c.language_settings.allowed_languages = resource.list;
      }
    },

    editTemplateRoleId: (
      state,
      action: PayloadAction<
        {
          resource: { id: string };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      state.c.role.id = resource.id;
      state.c.role_id = resource.id;
    },

    editTemplateRoleName: (
      state,
      action: PayloadAction<
        {
          resource: { name: string };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      state.c.role.name = resource.name;
    },

    updateTemplateMyHubertFlag: (
      state,
      action: PayloadAction<
        {
          resource: { isMyHubertEnabled: boolean };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      state.c.candidate_app = resource.isMyHubertEnabled;
    },

    updateTemplateCompanyName: (
      state,
      action: PayloadAction<
        {
          resource: { name: string };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      template.organization.name = resource.name;
      template.company = resource.name;
    },

    updateTemplateEstimatedLength: (
      state,
      action: PayloadAction<
        {
          resource: { estimatedLength: number };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      template.estimated_length = resource.estimatedLength;
    },

    updateTemplateSalaryRange: (
      state,
      action: PayloadAction<
        {
          resource: TTemplate['salary_range'];
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      template.salary_range = resource;
    },

    updateAccessGroupsVisibilityAndClientId: (
      state,
      action: PayloadAction<
        {
          resource: {
            accessGroupIds: string[];
            visibility: TVisibility | undefined;
            clientId: string;
          };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      template.accessGroupIds = resource.accessGroupIds;
      template.visibility = resource.visibility;
      template.client_id = resource.clientId;
    },

    /*****************************
     ****** EMAIL / SMS / SETTINGS ******
     *****************************/

    updateTemplateEmailTemplateOverride: (
      state,
      action: PayloadAction<
        {
          resource: {
            key: keyof NonNullable<TemplateEmailSettingsDocument['email_template_override']>;
            value: number | null;
          };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { key, value } = action.payload.resource;
      if (!template.email_settings) {
        template.email_settings = { email_template_override: {} };
      }
      if (!template.email_settings.email_template_override) {
        template.email_settings.email_template_override = {};
      }
      template.email_settings.email_template_override[key] = value;
    },

    updateTemplateSmsInvitation: (
      state,
      action: PayloadAction<
        {
          resource: Partial<TemplateSmsSettingsDocument['invitation']>;
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      if (!template.sms_settings) {
        template.sms_settings = {
          invitation: {
            enabled: false,
            delay_minutes: 0,
            template_id: '',
          },
        };
      }
      template.sms_settings.invitation = {
        ...template.sms_settings.invitation,
        ...resource,
      };
    },

    updateTemplateVoiceSettings: (
      state,
      action: PayloadAction<
        {
          resource: Partial<TemplateVoiceSettingsDocument>;
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      // Seed a full settings object (all fields required by TemplateVoiceSettingsDocument) when absent,
      // then merge the partial update in place — mutating the draft avoids the Partial-spread widening
      // that would otherwise make every field `| undefined`.
      Object.assign(
        (template.voice_settings ??= {
          web: false,
          speak_into_chat: false,
          listen_to_agent: false,
          stt_mode: 'batch',
          readback: false,
          phone: false,
          intent_triage: 'off',
          streaming_acks: false,
        }),
        resource,
      );
    },

    updateTemplateFeatureFlags: (
      state,
      action: PayloadAction<
        {
          /** Full flag objects keyed by flag name (e.g. language_switching) — merged into feature_flags. */
          resource: Record<string, unknown>;
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      template.feature_flags = {
        ...(template.feature_flags ?? {}),
        ...resource,
      };
    },

    updateTemplateInterviewBaseUrl: (
      state,
      action: PayloadAction<
        {
          resource: { interview_base_url: string };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { resource } = action.payload;
      if (!template.settings) {
        template.settings = { interview_base_url: '' };
      }
      template.settings.interview_base_url = resource.interview_base_url;
    },

    addTemplateReminderSetting: (
      state,
      action: PayloadAction<
        {
          resource: TemplateReminderSettingsDocument;
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      if (!template.reminder_settings) {
        template.reminder_settings = [];
      }
      template.reminder_settings.push(action.payload.resource);
    },

    updateTemplateReminderSettingByIndex: (
      state,
      action: PayloadAction<
        {
          resource: {
            index: number;
            reminder: TemplateReminderSettingsDocument;
          };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { index, reminder } = action.payload.resource;
      if (!template.reminder_settings || index < 0 || index >= template.reminder_settings.length) {
        toast.error(`Reminder not found.`);
        return;
      }
      template.reminder_settings[index] = reminder;
    },

    deleteTemplateReminderSettingByIndex: (
      state,
      action: PayloadAction<
        {
          resource: { index: number };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }
      const { index } = action.payload.resource;
      if (!template.reminder_settings || index < 0 || index >= template.reminder_settings.length) {
        toast.error(`Reminder not found.`);
        return;
      }
      template.reminder_settings.splice(index, 1);
    },

    /*****************************
     ********* PARAMETERS ********
     *****************************/

    /**
     * Deletion of a nodes parameter data
     */
    deleteNodeParameterData: (
      state,
      action: PayloadAction<{
        nodeId: string;
      }>,
    ) => {
      const { nodeId } = action.payload;
      // Remove any custom stuff
      const parameters = state.c.parameters;
      if (parameters && Object.prototype.hasOwnProperty.call(parameters, nodeId)) {
        delete parameters[nodeId];
      }
    },

    // Rules. `currentRules` — same reasoning as customQuestionRuleUpdate above — is what stops a
    // brand new rule from displacing the question's existing default rules in the override.
    addCustomQuestionRule(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            ruleType: ERuleType;
            currentRules: TQuestionRule[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, ruleType, currentRules } = resource;
      const rule = ruleType === ERuleType.DO_IF ? ruleType1() : ruleType2();

      pushRuleToNodeParams(state.c.parameters, nodeId, rule, currentRules);
    },

    // Optimistic local half of the early-reject checkbox: locally pushes the rule (and adds the
    // bridge question to the outline, purely so it's visible/editable — the interview runtime
    // resolves JUMP_TO by id regardless of outline membership) ahead of the single combined
    // apply-automatic-knockout request the appMiddleware listener below fires, which also forces
    // this option's evaluation to Disqualified server-side. Same `currentRules` reasoning as
    // addCustomQuestionRule above.
    applyAutomaticKnockout(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            optionId: string;
            rule: TQuestionRule;
            currentRules: TQuestionRule[];
            addQuestionIds?: string[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      pushRuleToNodeParams(state.c.parameters, resource.nodeId, resource.rule, resource.currentRules);

      if (resource.addQuestionIds) {
        for (const questionId of resource.addQuestionIds) {
          state.c.outline.question_list = appendToOutlineWithDedupRename(state.c.outline.question_list, questionId);
        }
      }
    },

    // `currentRules` seeds parameters[nodeId].rules from the question's own default rules when
    // there's no override yet (see ensureRulesOverride) — the same reasoning as
    // deleteAllCustomRules/deleteCustomQuestionRuleByIndex below, so editing a rule never assumes
    // an override was already created before it runs.
    customQuestionRuleUpdate(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            ruleIdx: number;
            rule: TQuestionRule;
            currentRules: TQuestionRule[];
            addQuestionIds?: string[];
            removeQuestionIds?: string[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, ruleIdx, rule, currentRules } = resource;
      const rules = ensureRulesOverride(state.c.parameters, nodeId, currentRules);
      rules[ruleIdx] = rule;
      const template = state.c;

      if (resource.addQuestionIds) {
        for (const questionId of resource.addQuestionIds) {
          template.outline.question_list = appendToOutlineWithDedupRename(template.outline.question_list, questionId);
        }
      }

      if (resource.removeQuestionIds) {
        for (const questionId of resource.removeQuestionIds) {
          template.outline.question_list = removeOneFromOutlineByPrefix(template.outline.question_list, questionId);
        }
      }
    },

    // `currentRules` is the rules array as currently displayed/in effect for this node — the
    // question's own default rules when there's no override yet, or the override's rules when
    // there is one (see getNodeRules) — passed in by the caller because this slice has no access
    // to the Library slice where the question's own rules live. Deleting from that array (rather
    // than assuming `parameters[nodeId].rules` already exists) is what makes this safe to call
    // whether or not an override exists yet, and whether or not that override happens to have a
    // `rules` key at all (parameters[nodeId] can exist for unrelated reasons, e.g. option overrides).
    deleteCustomQuestionRuleByIndex(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            ruleIdx: number;
            currentRules: TQuestionRule[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, ruleIdx, currentRules } = resource;
      const nextRules = [...currentRules];
      nextRules.splice(ruleIdx, 1);
      setRulesOnNodeParams(state.c.parameters, nodeId, nextRules);
    },

    // Same `currentRules` reasoning as deleteCustomQuestionRuleByIndex above.
    deleteAllCustomRules(
      state,
      action: PayloadAction<
        {
          resource: { nodeId: string; currentRules: TQuestionRule[] };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, currentRules } = resource;
      const template = state.c;
      let questionList = template.outline.question_list;
      for (const questionId of collectRuleJumpTargetIds(currentRules)) {
        questionList = removeOneFromOutlineByPrefix(questionList, questionId);
      }
      template.outline.question_list = questionList;
      setRulesOnNodeParams(state.c.parameters, nodeId, []);
    },

    // Rule conditions. Same `currentRules` reasoning as customQuestionRuleUpdate above.
    customQuestionRuleConditionPlus(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            ruleIdx: number;
            currentRules: TQuestionRule[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, ruleIdx, currentRules } = resource;
      const rules = ensureRulesOverride(state.c.parameters, nodeId, currentRules);
      const rule = rules[ruleIdx];
      if (!rule.conditions) {
        rules[ruleIdx] = {
          ...rule,
          conditions: [newCondition()],
        };
      } else {
        rule.conditions.push(newCondition());
      }
    },

    // Rule actions. Same `currentRules` reasoning as customQuestionRuleUpdate above.
    customQuestionRuleActionPlus(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            ruleIdx: number;
            currentRules: TQuestionRule[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, ruleIdx, currentRules } = resource;

      const rules = ensureRulesOverride(state.c.parameters, nodeId, currentRules);
      const rule = rules[ruleIdx];
      const x = rule.type === ERuleType.DO_IF ? newAction('JUMP_TO') : newAction();
      if (!rule.actions) {
        rules[ruleIdx] = {
          ...rule,
          actions: [x],
        };
      } else {
        rule.actions.push(x);
      }
    },

    customParametersUpdate(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            key: TTemplateParameterKeys;
            language?: TAvailableLanguageCodes;
            value: any;
            isDefault: boolean;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, key, value, language } = resource;
      const Params = state.c?.parameters;

      if (!Params) {
        toast.error(`Update failed: could not find the data.`);
        return;
      }

      // Pre, Post actions, options, min/max selections and min_required do not require language
      if (
        ['pre_actions', 'post_actions', 'options', 'min_selections', 'max_selections', 'min_required'].includes(key)
      ) {
        const nodeParams = Params[nodeId];
        if (!nodeParams) {
          Params[nodeId] = { [key]: value };
          return;
        }
        Params[nodeId][key] = value;
        return;
      }

      if (!language) {
        toast.error('No language given to custom params update.');
        return;
      }

      if (value === 'reset') {
        if (hasNestedKeys(Params, nodeId, key, language)) {
          delete Params[nodeId][key][language];
        } else {
          toast.error(`Params missing for ${nodeId}, ${key}.`);
        }
      } else {
        const nodeParams = Params[nodeId];
        if (!nodeParams) {
          Params[nodeId] = { [key]: { [language]: value } };
          return;
        }
        const keyParams = Params[nodeId][key];
        if (!keyParams) {
          Params[nodeId][key] = { [language]: value };
          return;
        }
        const keyLanguageParams = Params[nodeId][key][language];
        if (!keyLanguageParams) {
          Params[nodeId][key][language] = value;
          return;
        }
        Params[nodeId][key] = {
          ...Params[nodeId][key],
          [language]: value,
        };
      }
    },

    formParametersUpdateByFormId(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            formId: string;
            key: string;
            language?: TAvailableLanguageCodes;
            value: any;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, formId, key, value, language } = resource;
      const nodeParams = state.c.parameters[nodeId];

      if (language) {
        if (!nodeParams) {
          state.c.parameters[nodeId] = { [formId]: { [key]: { [language]: value } } };
          return;
        }

        const formParams = state.c.parameters[nodeId][formId];
        if (!formParams) {
          state.c.parameters[nodeId][formId] = { [key]: { [language]: value } };
          return;
        }

        const formKeyParams = state.c.parameters[nodeId][formId][key];
        if (!formKeyParams) {
          state.c.parameters[nodeId][formId][key] = { [language]: value };
          return;
        }

        state.c.parameters[nodeId][formId][key] = {
          ...state.c.parameters[nodeId][formId][key],
          [language]: value,
        };
      } else {
        if (!nodeParams) {
          state.c.parameters[nodeId] = { [formId]: { [key]: value } };
          return;
        }

        const formParams = state.c.parameters[nodeId][formId];
        if (!formParams) {
          state.c.parameters[nodeId][formId] = { [key]: value };
          return;
        }

        state.c.parameters[nodeId][formId] = {
          ...state.c.parameters[nodeId][formId],
          [key]: value,
        };
      }
    },

    updateOptionEnabledInParameters(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            optionId: string;
            language: TAvailableLanguageCodes;
            value: boolean;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, optionId, language, value } = resource;
      const parameters = state.c?.parameters;
      if (!parameters) {
        toast.error(`Update failed: could not find the data.`);
        return;
      }
      if (!parameters[nodeId]) {
        parameters[nodeId] = {};
      }
      if (!parameters[nodeId].options) {
        parameters[nodeId].options = {};
      }
      if (!parameters[nodeId].options[optionId]) {
        parameters[nodeId].options[optionId] = {};
      }
      if (!parameters[nodeId].options[optionId].enabled) {
        parameters[nodeId].options[optionId].enabled = {};
      }
      parameters[nodeId].options[optionId].enabled[language] = value;
    },

    setReplyOptionsInParameters(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            optionId: string;
            language: TAvailableLanguageCodes;
            replies: string[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, optionId, language, replies: nextReplies } = resource;
      const parameters = state.c?.parameters;
      if (!parameters) {
        toast.error(`Update failed: could not find the data.`);
        return;
      }
      if (!parameters[nodeId]) {
        parameters[nodeId] = {};
      }
      if (!parameters[nodeId].options) {
        parameters[nodeId].options = {};
      }
      if (!parameters[nodeId].options[optionId]) {
        parameters[nodeId].options[optionId] = {};
      }
      if (!parameters[nodeId].options[optionId].replies) {
        parameters[nodeId].options[optionId].replies = {};
      }

      parameters[nodeId].options[optionId].replies[language] = [...nextReplies];
    },

    /*****************************
     ******* END PARAMETERS *******
     *****************************/

    /*****************************
     ********* EVALUATION ********
     *****************************/

    /**
     * Deletion of a nodes evaluation data
     */
    deleteNodeEvaluationData: (
      state,
      action: PayloadAction<{
        nodeId: string;
        type: EQuestionType;
        scoringCategory?: string;
      }>,
    ) => {
      const { nodeId, type, scoringCategory } = action.payload;
      const evaluation = state.c.evaluation;
      if (evaluation && Object.prototype.hasOwnProperty.call(evaluation, nodeId)) {
        delete evaluation[nodeId];
      }

      if (type === EQuestionType.Open && scoringCategory && scoringCategory.length) {
        for (const categories of [evaluation.scoring_categories, state.c.scoringCategories]) {
          const index = categories.list.findIndex((x) => x === scoringCategory);
          if (index > -1) {
            categories.list.splice(index, 1);
          }
          delete categories[scoringCategory];
        }
      }
    },

    setCustomEvaluation(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            nodeType: EQuestionType;
            formType?: EFormType;
            key: string;
            evaluation: any;
            removeRuleIds?: string[];
            currentRules?: TQuestionRule[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, nodeType, key, evaluation, removeRuleIds, currentRules } = resource;
      const Evaluation = state.c.evaluation;
      const nodeEvaluationSummary = state.evaluationSummary[nodeId];

      if (!('scoring_categories' in Evaluation)) {
        (Evaluation as any).scoring_categories = { list: [] };
      }

      Evaluation[nodeId] = {
        ...Evaluation[nodeId],
        [key]: { evaluation },
      };

      if (!nodeEvaluationSummary.evaluation) {
        nodeEvaluationSummary.evaluation = {};
      }
      nodeEvaluationSummary.evaluation[key] = evaluation;
      if (nodeType === EQuestionType.SingleSelect) {
        updateSingleSelectEvaluationSummary(nodeEvaluationSummary);
      } else if (nodeType === EQuestionType.MultipleSelect) {
        updateMultipleSelectEvaluationSummary(nodeEvaluationSummary);
      } else if (nodeType === EQuestionType.Form) {
        updateFormSliderEvaluationSummary(nodeEvaluationSummary, nodeId);
      }

      if (removeRuleIds?.length && currentRules) {
        setRulesOnNodeParams(state.c.parameters, nodeId, removeRulesByIds(currentRules, removeRuleIds));
      }
    },

    resetCustomEvaluation(
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            customKey: string;
            languages: string[];
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { nodeId, customKey, languages } = resource;
      const Evaluation = state.c.evaluation;
      try {
        languages.map((language: string) => {
          delete Evaluation[nodeId][language][customKey];
        });
      } catch {
        toast.error('Something went wrong while resetting evaluation to original.');
      }
    },

    // Scoring categories
    updateScoringCategories(
      state,
      action: PayloadAction<{
        nodeId: string;
        scoringCategory: string;
        weight: number;
        parentId?: string;
      }>,
    ) {
      const { nodeId, scoringCategory, weight, parentId } = action.payload;
      const Evaluation = state.c.evaluation;
      const scoringCategories = Evaluation.scoring_categories;
      const scoringCategories2 = state.c.scoringCategories;
      console.log(`updateScoringCategories ${nodeId}`, parentId);

      if (parentId && parentId.length) {
        updateFollowupScoringCategory(scoringCategories, scoringCategory, nodeId, weight, parentId);
        updateFollowupScoringCategory(scoringCategories2, scoringCategory, nodeId, weight, parentId);
      } else {
        updateNonFollowupScoringCategory(scoringCategories, scoringCategory, nodeId, weight);
        updateNonFollowupScoringCategory(scoringCategories2, scoringCategory, nodeId, weight);
      }
    },

    updateCategoryWeight(
      state,
      action: PayloadAction<{
        resource: {
          nodeId: string;
          scoringCategoryId: string;
          weight: number;
        };
      }>,
    ) {
      const { resource } = action.payload;
      const { scoringCategoryId, weight } = resource;
      const Evaluation = state.c.evaluation;
      const categories = Evaluation.scoring_categories;
      const category = categories[scoringCategoryId];
      category.weight = weight;

      const cats = state.c.scoringCategories;
      const cat = cats[scoringCategoryId];
      cat.weight = weight;
    },

    updateSelectionCountCriteria(
      state,
      action: PayloadAction<{
        nodeId: string;
        selectionCountCriteria: TSelectionCountCriteria[];
      }>,
    ) {
      const { nodeId, selectionCountCriteria } = action.payload;
      const evaluation = state.c?.evaluation;
      if (!evaluation) {
        toast.error('Evaluation not found.');
        return;
      }

      if (!evaluation[nodeId]) {
        evaluation[nodeId] = {};
      }

      evaluation[nodeId].selection_count = selectionCountCriteria;
    },

    /*****************************
     ******* END EVALUATION *******
     *****************************/

    /*****************************
     ****** TEMPLATE BLOCKS ******
     *****************************/
    editBlockName: (
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            blockIndex: number;
            name: string;
          };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      const { blockId, blockIndex, name } = resource;
      const block = state.c.blocks.blocks[blockIndex];
      if (block.id === blockId) {
        state.c.blocks.blocks[blockIndex].name = name;
      } else {
        toast.error('Mismatching block.');
      }
      // state.c.blocks.map[resource.blockId].name = name;
    },

    addBaseBlock(
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            blockName: string;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockName } = resource;
      let { blockId } = resource;
      const blocks = state.c?.blocks?.blocks;
      if (!blocks) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }

      const block = blocks.find((x) => x.id === blockId);

      if (block) {
        blockId = nanoid();
        toast.warning(`Update failed: block with ID ${blockId} already existed. Creating a new one.`);
      }

      blocks.push({
        id: blockId,
        name: blockName,
        description: '',
        is_open: true,
        editable: true,
        nodes: [],
      });
    },

    updateBaseBlocks(
      state,
      action: PayloadAction<
        {
          resource: {
            blockIds: string[];
            items: Record<string, TNode[]>;
            deleteBlockId?: string;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockIds, items, deleteBlockId } = resource;

      const blocks = state.c?.blocks.blocks;
      if (!blocks) {
        toast.error(`Update failed: could not find the data.`);
        return;
      }
      const blockIdToBlockMap: Record<string, TTemplateBlock> = {};
      blocks.map((block) => {
        blockIdToBlockMap[block.id] = block;
      });

      const temp: TTemplateBlock[] = [];

      blockIds.map((blockId) => {
        if (!deleteBlockId || blockId !== deleteBlockId) {
          const existingBlock = blockIdToBlockMap[blockId];
          const newBlock: TTemplateBlock = {
            id: blockId,
            name: existingBlock.name,
            description: existingBlock.description,
            is_open: existingBlock.is_open,
            editable: existingBlock.editable,
            nodes: [],
          };

          const nodes = items[blockId];
          nodes.map((node) => {
            if (!node) return;
            for (const [_, value] of Object.entries(blockIdToBlockMap)) {
              const x = value.nodes.find((x) => x.id === node.id);
              if (x) {
                newBlock.nodes.push(x);
              }
            }
          });

          temp.push(newBlock);
        }
      });

      if (deleteBlockId) {
        const removedBlock = blockIdToBlockMap[deleteBlockId];
        const nodes = items[deleteBlockId];

        if (nodes.length) {
          const newBlock: TTemplateBlock = {
            id: nanoid(),
            name: `From deleted block ${removedBlock.name}`,
            description: '',
            is_open: true,
            editable: true,
            nodes: [],
          };

          nodes.map((node) => {
            for (const [_, value] of Object.entries(blockIdToBlockMap)) {
              const x = value.nodes.find((x) => x.id === node.id);
              if (x) {
                newBlock.nodes.push(x);
              }
            }
          });
          temp.unshift(newBlock);
        }
      }

      if (state.c) {
        state.c.blocks = {
          ...state.c?.blocks,
          blocks: temp,
        };
      }
    },

    deleteBlock: (
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
          };
        } & BasePayload
      >,
    ) => {
      const { resource } = action.payload;
      if (state.c.blocks.ids) {
        const idx = state.c.blocks.ids.indexOf(resource.blockId);
        if (idx > -1) {
          if (state.c.blocks.map) {
            delete state.c.blocks.map[resource.blockId];
          }
          state.c.blocks.ids.splice(idx, 1);
        }
      }
      // TODO: Side effects
      /**
       * Remove questions
       *  - ids from qutter_list, action_list
       *  - From evaluation {}
       *  - From parameters {}
       *  - From outline {}
       */
    },

    /**
     * Deletion of a base node
     */
    deleteNodeFromBlock: (
      state,
      action: PayloadAction<
        {
          resource: {
            nodeId: string;
            blockId: string;
            question: TAnyClientQuestionType;
          };
        } & BasePayload
      >,
    ) => {
      update(state).removeNodeFromBlock(action.payload.resource);
      // NOTE: See appMiddleware for eventual side effects!!
    },

    /**
     * Adds a base node to block
     */
    addNodeToBlock: (
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            nodeIdx: number;
            newNode: TNode;
            question?: TAnyClientQuestionType;
          };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }

      const { resource } = action.payload;
      const node = Object.assign({}, resource.newNode);
      const nodeId = resource.newNode.id;

      const list = template.outline.question_list;
      const exists = list.filter((x) => x.includes(nodeId));
      if (exists && exists.length > 0) {
        const extra = makeid(4);
        node.id = `${node.id}|${extra}`;
      }

      // Deliberately does NOT copy the question's rules into parameters — same reasoning as the
      // backend's addSingleQuestionToDraft (see templates.helper.ts): doing so unconditionally
      // would create an override (even an empty one, which still wins outright per getNodeRules'
      // precedence) that permanently shadows the question's own rules for this template, even
      // when nothing about this template needs a rules customization yet.
      const block = template.blocks.blocks.find((x) => x.id === resource.blockId);
      if (!block) {
        toast.error(`Could not find the block - abort.`);
        return;
      }
      block.nodes.splice(resource.nodeIdx + 1, 0, node);

      // Add to general question list
      template.outline.question_list.push(node.id);

      // NOTE: See appMiddleware for side effects!
    },

    addQuestionToBlock(
      _state,
      _action: PayloadAction<
        {
          resource: {
            blockId: string;
            nodeIdx: number;
            newNode: TNode;
            question?: TAnyClientQuestionType;
          };
        } & BasePayload
      >,
    ) {
      console.info('addQuestionToBlock');
      // NOTE: See appMiddleware for side effects!
    },
    removeQuestionFromBlock(
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            nodeId: string;
          };
        } & BasePayload
      >,
    ) {
      console.info('removeQuestionFromBlock');
      update(state).removeNodeFromBlock(action.payload.resource);
      // NOTE: See appMiddleware for side effects!
    },

    /**
     * Quick messages live entirely on the node (data.text) — no questions-collection
     * document and no outline.question_list entry (consumers of question_list expect
     * every id to resolve to a library question).
     */
    addQuickMessageToBlock: (
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            nodeIdx: number;
            newNode: TQuickMessageNode;
          };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }

      const { resource } = action.payload;
      const block = template.blocks.blocks.find((x) => x.id === resource.blockId);
      if (!block) {
        toast.error(`Could not find the block - abort.`);
        return;
      }
      block.nodes.splice(resource.nodeIdx + 1, 0, resource.newNode);
    },

    updateQuickMessageText: (
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            nodeId: string;
            text: TQuickMessageNode['data']['text'];
          };
        } & BasePayload
      >,
    ) => {
      const template = state.c;
      if (!template) {
        toast.error(`Template not available.`);
        return;
      }

      const { resource } = action.payload;
      const block = template.blocks.blocks.find((x) => x.id === resource.blockId);
      if (!block) {
        toast.error(`Could not find the block - abort.`);
        return;
      }

      const node = block.nodes.find((x) => x.id === resource.nodeId);
      if (!node || !isQuickMessageNode(node)) {
        toast.error(`Could not find the quick message in the block - abort.`);
        return;
      }
      node.data.text = resource.text;
    },

    deleteQuickMessageFromBlock: (
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            nodeId: string;
          };
        } & BasePayload
      >,
    ) => {
      update(state).removeNodeFromBlock(action.payload.resource);
    },
    /*****************************
     **** END TEMPLATE BLOCKS ****
     *****************************/

    /*****************************
     ***** Interview Summary ******
     *****************************/
    updateInterviewSummaryBlockName(
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            value: string;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockId, value } = resource;
      const summaryBlocks = state.c?.interview_summary?.summary_blocks;
      if (!summaryBlocks) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }
      const block = summaryBlocks.find((x) => x.id === blockId);
      if (!block) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }
      block.name = value;
    },

    updateInterviewSummaryDetailLabelByQuestionId(
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            questionId: string;
            newLabel: string;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockId, questionId, newLabel } = resource;
      const summaryBlocks = state.c?.interview_summary?.summary_blocks;
      if (!summaryBlocks) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }

      const block = summaryBlocks.find((x) => x.id === blockId);
      if (!block) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }

      const detail = block.details.find((x) => x.id === questionId);
      if (!detail) {
        toast.error(`Update failed: could not find block detail ${questionId} in ${blockId}.`);
        return;
      }
      detail.label = newLabel;
    },

    updateQuestionInterviewSummaryBlock(
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            questionId: string;
            newBlockId: string;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockId, questionId, newBlockId } = resource;
      const summaryBlocks = state.c?.interview_summary?.summary_blocks;
      if (!summaryBlocks) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }

      const oldBlock = summaryBlocks.find((x) => x.id === blockId);
      if (!oldBlock) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }

      const index = oldBlock.details.findIndex((y) => y.id === questionId);
      if (index > -1) {
        const detail = oldBlock.details[index];
        oldBlock.details.splice(index, 1);

        const newBlock = summaryBlocks.find((x) => x.id === newBlockId);
        if (!newBlock) {
          toast.error(`Update failed: could not find block ${blockId}.`);
          return;
        }

        newBlock.details.push(detail);
      }
    },

    addInterviewSummaryBlock(
      state,
      action: PayloadAction<
        {
          resource: {
            blockId: string;
            blockName: string;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockName } = resource;
      let { blockId } = resource;
      const summaryBlocks = state.c?.interview_summary?.summary_blocks;
      if (!summaryBlocks) {
        toast.error(`Update failed: could not find block ${blockId}.`);
        return;
      }

      const block = summaryBlocks.find((x) => x.id === blockId);

      if (block) {
        blockId = nanoid();
        toast.warning(`Update failed: block with ID ${blockId} already existed. Creating a new one.`);
      }

      summaryBlocks.push({
        id: blockId,
        name: blockName,
        details: [],
      });
    },

    updateInterviewSummaryBlocks(
      state,
      action: PayloadAction<
        {
          resource: {
            blockIds: string[];
            items: Record<string, string[]>;
            deleteBlockId?: string;
            /** Names for block ids that do not exist yet, to create and fill a block in one update. */
            newBlockNames?: Record<string, string>;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { blockIds, items, deleteBlockId, newBlockNames } = resource;

      const summaryBlocks = state.c?.interview_summary?.summary_blocks;
      if (!summaryBlocks) {
        toast.error(`Update failed: could not find the data.`);
        return;
      }

      const blockIdToBlockMap: Record<string, TTemplateInterviewSummaryBlock> = {};

      summaryBlocks.map((block) => {
        blockIdToBlockMap[block.id] = block;
      });

      const temp: TTemplateInterviewSummaryBlock[] = [];

      // Details the caller listed nowhere are hidden, not gone - usually soft deleted ones.
      const listedDetailIds = new Set<string>();
      Object.values(items).forEach((detailIds) => detailIds?.forEach((detailId) => listedDetailIds.add(detailId)));

      blockIds.map((blockId) => {
        if (!deleteBlockId || blockId !== deleteBlockId) {
          const existingBlock = blockIdToBlockMap[blockId];

          const newBlockName = newBlockNames?.[blockId];

          // Unknown block and no name to create it with: skip it rather than throw.
          if (!existingBlock && !newBlockName) {
            return;
          }

          const newBlock: TTemplateInterviewSummaryBlock = {
            id: blockId,
            name: existingBlock ? existingBlock.name : (newBlockName as string),
            details: [],
          };

          const detailIds = items[blockId] ?? [];
          detailIds.map((detailId) => {
            // Check if the detail already exists in this block's details
            const detailAlreadyInBlock = newBlock.details.some((detail) => detail.id === detailId);
            if (detailAlreadyInBlock) {
              return;
            }

            // This block's own copy wins: with the same id stored in two blocks, taking the
            // first match anywhere can overwrite a live question with its soft deleted twin.
            const detail =
              existingBlock?.details.find((x) => x?.id === detailId) ??
              Object.values(blockIdToBlockMap)
                .flatMap((block) => block.details)
                .find((x) => x?.id === detailId);

            if (detail) {
              newBlock.details.push(detail);
            }
          });

          existingBlock?.details.forEach((detail, index) => {
            if (detail && detail.id && !listedDetailIds.has(detail.id)) {
              newBlock.details.splice(Math.min(index, newBlock.details.length), 0, detail);
            }
          });

          temp.push(newBlock);
        }
      });

      state.c.interview_summary = {
        ...state.c.interview_summary,
        summary_blocks: temp,
      };
    },

    /*****************************
     *** END Interview Summary ****
     *****************************/

    pushToChangeLog: (
      state,
      action: PayloadAction<{
        templateId: string;
        item: TTemplateChangeLogItem;
      }>,
    ) => {
      const { item } = action.payload;
      if (state.c.change_log) {
        state.c.change_log.push(item);
      } else {
        state.c.change_log = [item];
      }
    },

    /**
     * Updates a templates basic root fields.
     * Allowed are only the resource.key values.
     */
    updateTemplate(
      state,
      action: PayloadAction<
        {
          resource: {
            key: 'name' | 'threshold' | 'description';
            value: string | number;
          };
        } & BasePayload
      >,
    ) {
      const { resource } = action.payload;
      const { key, value } = resource;
      if (key === 'name') {
        state.c.name = value as string;
      } else if (key === 'description') {
        state.c.description = value as string;
      } else if (key === 'threshold') {
        state.c.threshold = value as number;
      }
    },
  },
});

export const {
  /******* General Functionality *******/
  addTemplate,
  removeAudienceProblemsFixedBySharing,
  updateProcessId,

  setCurrentTemplateLanguage,
  setQuestionTranslationErrors,
  setQuestionTranslationResults,
  updateTemplateLanguageList,
  updateTemplateAllowedLanguageList,
  editTemplateRoleId,
  editTemplateRoleName,
  updateTemplateMyHubertFlag,
  updateTemplateCompanyName,
  updateTemplateSalaryRange,
  updateTemplateEstimatedLength,
  updateAccessGroupsVisibilityAndClientId,
  /******* EMAIL / SMS / SETTINGS *******/
  updateTemplateEmailTemplateOverride,
  updateTemplateSmsInvitation,
  updateTemplateVoiceSettings,
  updateTemplateFeatureFlags,
  updateTemplateInterviewBaseUrl,
  addTemplateReminderSetting,
  updateTemplateReminderSettingByIndex,
  deleteTemplateReminderSettingByIndex,
  /******* TEMPLATE BLOCKS *******/
  editBlockName,
  addBaseBlock,
  updateBaseBlocks,

  // createBlock,
  deleteBlock,
  deleteNodeFromBlock,
  addNodeToBlock,
  addQuestionToBlock,
  removeQuestionFromBlock,
  addQuickMessageToBlock,
  updateQuickMessageText,
  deleteQuickMessageFromBlock,
  /******* END TEMPLATE BLOCKS *******/

  /********* PARAMETERS *********/
  deleteNodeParameterData,

  // Rules
  addCustomQuestionRule,
  applyAutomaticKnockout,
  customQuestionRuleUpdate,
  deleteCustomQuestionRuleByIndex,
  deleteAllCustomRules,
  // Rule conditions
  customQuestionRuleConditionPlus,
  // Rule actions
  customQuestionRuleActionPlus,
  // Custom other
  customParametersUpdate,
  // Custom form parameters
  formParametersUpdateByFormId,
  updateOptionEnabledInParameters,
  setReplyOptionsInParameters,
  /******* END PARAMETERS *******/

  /********* EVALUATION *********/
  deleteNodeEvaluationData,
  setCustomEvaluation,
  resetCustomEvaluation,
  updateScoringCategories,
  updateCategoryWeight,
  updateSelectionCountCriteria,
  /******* END EVALUATION *******/

  /***** Interview Summary ******/
  addInterviewSummaryBlock,
  updateInterviewSummaryBlockName,
  updateInterviewSummaryDetailLabelByQuestionId,
  updateQuestionInterviewSummaryBlock,
  updateInterviewSummaryBlocks,
  /** END Interview Summary ****/

  updateTemplate,
  pushToChangeLog,
} = TemplateSlice.actions;
export default TemplateSlice.reducer;
